import { defaultGenId, wrapRunner } from './util.js';
export class PriorityExecutorRunner {
    #executor;
    #level;
    constructor(executor, level) {
        if (level < 0)
            throw new Error('level must not be lower than 0');
        this.#executor = executor;
        this.#level = level;
    }
    get executor() {
        return this.#executor;
    }
    get level() {
        return this.#level;
    }
    async run(fn) {
        return this.#executor.scheduleAndWait(this.#level, fn);
    }
    wrap(fn) {
        return wrapRunner(fn, this);
    }
}
export class PriorityExecutor {
    #levels;
    #concurrencyLimit;
    #running = new Map();
    #runningCount = 0;
    #reserveHighPrioritySlots;
    #genId;
    #onRun = new Set();
    #onSchedule = new Set();
    constructor({ maxLevel = 1, concurrencyLimit = 1, genId = defaultGenId, reserveHighPrioritySlots = concurrencyLimit >= Math.floor(maxLevel / 2), } = {}) {
        if (concurrencyLimit < 1)
            throw new Error('concurrencyLimit must be at least 1');
        if (maxLevel < 0)
            throw new Error('maxLevel must be at least 0');
        if (reserveHighPrioritySlots) {
            const minSlots = reserveHighPrioritySlots === true
                ? Math.floor(maxLevel / 2)
                : reserveHighPrioritySlots.length;
            if (concurrencyLimit < minSlots)
                throw new Error(`not enough slots for reserveHighPrioritySlots: max level is ${maxLevel} with ${concurrencyLimit} slots, need at least ${minSlots} slots to work`);
        }
        this.#levels = Array(maxLevel + 1)
            .fill(null)
            .map(() => []);
        this.#concurrencyLimit = concurrencyLimit;
        this.#genId = genId;
        if (reserveHighPrioritySlots === true) {
            this.#reserveHighPrioritySlots = [];
            for (let i = 0; i < maxLevel; i += 2)
                this.#reserveHighPrioritySlots.push(i);
        }
        else if (reserveHighPrioritySlots) {
            this.#reserveHighPrioritySlots = [...reserveHighPrioritySlots];
        }
        else {
            this.#reserveHighPrioritySlots = null;
        }
    }
    get maxLevel() {
        return this.#levels.length - 1;
    }
    get concurrencyLimit() {
        return this.#concurrencyLimit;
    }
    get running() {
        return this.#runningCount;
    }
    get reserveHighPrioritySlots() {
        return !!this.#reserveHighPrioritySlots;
    }
    runner(level) {
        return new PriorityExecutorRunner(this, level);
    }
    cancel(task) {
        const level = this.#levels.at(task.level);
        if (!level)
            throw new Error('invalid TaskRef');
        const idx = level.findIndex(x => x.id === task.id);
        if (idx === -1)
            return false;
        level.splice(idx, 1);
        return true;
    }
    #parseParams(levelOrParams, fn) {
        const params = typeof levelOrParams === 'object'
            ? levelOrParams
            : { level: levelOrParams };
        const { level, name = fn.name || '<unnamed>' } = params;
        return { level, name };
    }
    schedule(levelOrParams, fn) {
        const params = this.#parseParams(levelOrParams, fn);
        if (params.level < 0) {
            throw new Error('level must not be lower than 0');
        }
        if (params.level > this.maxLevel) {
            throw new Error(`level must be lower than or equal to max (${this.maxLevel})`);
        }
        const id = (0, this.#genId)(); // to call it without `this`
        const task = {
            ...params,
            id,
            fn,
        };
        this.#dispatchSchedule({
            id: task.id,
            name: task.name,
            level: task.level,
        });
        this.#levels[params.level].push(task);
        queueMicrotask(() => this.#runNext());
        return {
            id: task.id,
            level: task.level,
        };
    }
    async scheduleAndWait(levelOrParams, fn) {
        return new Promise((ok, ko) => {
            this.schedule(this.#parseParams(levelOrParams, fn), async () => {
                try {
                    ok(fn());
                }
                catch (e) {
                    ko(e);
                }
            });
        });
    }
    get freeSlots() {
        let reallyFreeSlots = this.#concurrencyLimit - this.#runningCount;
        if (reallyFreeSlots < 0)
            reallyFreeSlots = 0;
        let freeSlots = reallyFreeSlots;
        if (!this.#reserveHighPrioritySlots) {
            return { free: freeSlots, reserved: [] };
        }
        const runningLevels = [...this.#running.values()]
            .map(x => x.level)
            .sort((a, b) => a - b);
        const reserved = [...this.#reserveHighPrioritySlots];
        if (freeSlots) {
            for (let i = 0; i < reserved.length;) {
                // eslint-disable-next-line security/detect-object-injection
                const level = reserved[i];
                if (runningLevels.length && runningLevels[0] <= level) {
                    reserved.splice(i, 1);
                    runningLevels.shift();
                }
                else {
                    i++;
                    freeSlots--;
                }
            }
        }
        if (freeSlots < 0)
            freeSlots = 0;
        return { free: freeSlots, reserved: reserved.slice(0, reallyFreeSlots) };
    }
    get slots() {
        const running = [...this.#running.values()].map(task => ({
            id: task.id,
            name: task.name,
            level: task.level,
        }));
        while (running.length < this.concurrencyLimit)
            running.push(null);
        return running;
    }
    #getNextTask() {
        const { free, reserved } = this.freeSlots;
        for (const [levelI, level] of this.#levels.entries()) {
            if (!free && !reserved.some(x => levelI <= x))
                return undefined;
            if (level.length)
                return level.shift();
        }
        return undefined;
    }
    #runNext() {
        if (this.#runningCount >= this.#concurrencyLimit)
            return;
        const task = this.#getNextTask();
        if (!task)
            return;
        this.#dispatchRun({
            id: task.id,
            name: task.name,
            level: task.level,
        });
        this.#runningCount++;
        const promise = new Promise(ok => ok(task.fn()));
        this.#running.set(promise, task);
        void promise.finally(() => {
            this.#running.delete(promise);
            this.#runningCount--;
            this.#runNext();
        });
    }
    onSchedule(fn) {
        this.#onSchedule.add(fn);
        return () => this.offSchedule(fn);
    }
    offSchedule(fn) {
        this.#onSchedule.delete(fn);
    }
    #dispatchSchedule(task) {
        for (const handler of [...this.#onSchedule]) {
            try {
                handler(task);
            }
            catch (e) {
                console.error('in dispatchSchedule', e);
            }
        }
    }
    onRun(fn) {
        this.#onRun.add(fn);
        return () => this.offRun(fn);
    }
    offRun(fn) {
        this.#onRun.delete(fn);
    }
    #dispatchRun(task) {
        for (const handler of [...this.#onRun]) {
            try {
                handler(task);
            }
            catch (e) {
                console.error('in dispatchRun', e);
            }
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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